// @ts-nocheck
/**
 * Structural validation for elements[].predefinedCanvasItems (DEC-045).
 */

import type { ValidationResult } from '../../diagnostics';
import { addIssue } from '../../diagnostics';
import type { ElementDefinition } from '../../types/element';
import { isDefaultElementId } from '../../../utils/TemplateIdUtils';
import { validateElementItemDefaults } from './defaultValueValidation';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function validatePredefinedCanvasItems(
  element: ElementDefinition,
  elementIndex: number,
  elementById: ReadonlyMap<string, ElementDefinition>,
  propertyTypeByKey: Map<string, string> | ReadonlyMap<string, string>,
  result: ValidationResult,
): ValidationResult {
  const seeds = element.predefinedCanvasItems;
  if (seeds === undefined) return result;

  const elPath = `elements[${elementIndex}]`;
  if (!Array.isArray(seeds)) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: `Element predefinedCanvasItems must be an array`,
      path: `${elPath}.predefinedCanvasItems`,
    });
  }

  let next = result;
  const seenIds = new Set<string>();

  for (let i = 0; i < seeds.length; i++) {
    const seedPath = `${elPath}.predefinedCanvasItems[${i}]`;
    const seed = seeds[i];

    if (!isPlainObject(seed)) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Predefined canvas item seed must be a plain object`,
        path: seedPath,
      });
      continue;
    }

    const row = seed as Record<string, unknown>;

    if (typeof row.id !== 'string' || row.id.trim() === '') {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Predefined canvas item seed is missing required field 'id'`,
        path: `${seedPath}.id`,
      });
    } else if (seenIds.has(row.id)) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.duplicate_id',
        message: `Duplicate predefined canvas item seed id '${row.id}' on element '${element.id}'`,
        path: `${seedPath}.id`,
      });
    } else {
      seenIds.add(row.id);
    }

    const targetElementId = row.targetElementId;
    if (typeof targetElementId !== 'string' || targetElementId.trim() === '') {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: `Predefined canvas item seed is missing required field 'targetElementId'`,
        path: `${seedPath}.targetElementId`,
      });
    } else if (isDefaultElementId(targetElementId)) {
      // Generic ItemCanvasElement sentinel (DEC-050) — no element lookup required.
    } else if (!elementById.has(targetElementId)) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `Predefined canvas item seed references unknown targetElementId '${targetElementId}'`,
        path: `${seedPath}.targetElementId`,
      });
    } else if (targetElementId === element.id) {
      next = addIssue(next, {
        severity: 'warning',
        code: 'schema.deprecated_field',
        message: `Predefined canvas item seed on element '${element.id}' targets the same element type; runtime cycle guard limits recursion`,
        path: `${seedPath}.targetElementId`,
      });
    }

    if (row.itemDefaults !== undefined) {
      next = validateElementItemDefaults(
        row.itemDefaults,
        `${seedPath}.itemDefaults`,
        propertyTypeByKey,
        next,
      );

      const itemDefaults = row.itemDefaults as Record<string, unknown>;
      const itemFields = itemDefaults.itemFields;
      if (isPlainObject(itemFields) && itemFields.layoutInUse !== undefined) {
        const layoutInUse = itemFields.layoutInUse;
        if (typeof layoutInUse !== 'number' || !Number.isInteger(layoutInUse) || layoutInUse < 0) {
          next = addIssue(next, {
            severity: 'error',
            code: 'schema.invalid_field_type',
            message: `layoutInUse must be a non-negative integer`,
            path: `${seedPath}.itemDefaults.itemFields.layoutInUse`,
          });
        } else if (typeof targetElementId === 'string' && elementById.has(targetElementId)) {
          const targetElement = elementById.get(targetElementId)!;
          // layoutInUse indexes cyclable layouts only (placeholder layouts excluded — DEC-062).
          const layoutCount = (targetElement.layouts ?? []).filter(
            (layout) => layout.isPlaceholderLayout !== true,
          ).length;
          if (layoutCount === 0 || layoutInUse >= layoutCount) {
            next = addIssue(next, {
              severity: 'error',
              code: 'schema.invalid_default_value',
              message: `layoutInUse ${layoutInUse} is out of range for target element '${targetElementId}' (${layoutCount} layout(s))`,
              path: `${seedPath}.itemDefaults.itemFields.layoutInUse`,
            });
          }
        }
      }
    }
  }

  return next;
}
